import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  API_PATHS,
  type AssignmentDto,
  type ChangelogEntryDto,
  type PersonDto,
  type SessionUserDto,
  type SlotRange,
} from '@betreuung/shared';
import { apiFetch } from './http.js';
import { queryKeys } from './queryKeys.js';

/**
 * Server state.
 *
 * Assignments are fetched per window (month grid, year, statistics period) and
 * clipped on the client; the payload for a whole year is a few tens of kilobytes,
 * so aggregating locally is cheaper than a dedicated statistics endpoint.
 *
 * Freshness comes from the event stream, which invalidates these queries — the
 * generous `staleTime` avoids redundant refetches in between.
 */
const STALE_TIME = 5 * 60 * 1000;

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => apiFetch<{ user: SessionUserDto }>(API_PATHS.me).then((body) => body.user),
    staleTime: STALE_TIME,
    // A missing session is a normal state, not a transient failure.
    retry: false,
  });
}

export function usePersons(enabled = true) {
  return useQuery({
    queryKey: queryKeys.persons,
    queryFn: () => apiFetch<{ persons: PersonDto[] }>(API_PATHS.persons).then((body) => body.persons),
    staleTime: STALE_TIME,
    enabled,
  });
}

export function useAssignmentRange(range: SlotRange | null) {
  return useQuery({
    queryKey: range ? queryKeys.assignments.range(range.fromSlot, range.toSlot) : ['assignments', 'range', 'idle'],
    queryFn: () =>
      apiFetch<{ assignments: AssignmentDto[] }>(
        `${API_PATHS.assignments}?from=${range!.fromSlot}&to=${range!.toSlot}`,
      ).then((body) => body.assignments),
    staleTime: STALE_TIME,
    enabled: range !== null,
  });
}

const UPCOMING_PAGE_SIZE = 30;

export function useUpcomingAssignments() {
  return useInfiniteQuery({
    queryKey: queryKeys.assignments.upcoming,
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ limit: String(UPCOMING_PAGE_SIZE) });
      if (pageParam !== null) query.set('cursor', String(pageParam));
      return apiFetch<{ assignments: AssignmentDto[]; nextCursor: number | null }>(
        `${API_PATHS.upcoming}?${query.toString()}`,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: STALE_TIME,
  });
}

const CHANGELOG_PAGE_SIZE = 30;

export function useChangelog(enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.changelog,
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ limit: String(CHANGELOG_PAGE_SIZE) });
      if (pageParam !== null) query.set('cursor', String(pageParam));
      return apiFetch<{ entries: ChangelogEntryDto[]; nextCursor: number | null }>(
        `${API_PATHS.changelog}?${query.toString()}`,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: STALE_TIME,
    enabled,
  });
}

/** Flattens the pages of an infinite query into one ordered list. */
export function flattenPages<T, K extends string>(
  pages: { [P in K]: T[] }[] | undefined,
  key: K,
): T[] {
  if (!pages) return [];
  return pages.flatMap((page) => page[key]);
}
